import { useState, useEffect } from "react"
import { Shield, Eye, Lock, Database, Users, Mail, ArrowRight, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Link } from "react-router-dom"
import Seo from "@/seo/seo"
import { routeSeoConfig } from "@/utils/seo-config"
import axios from "@/utils/axios"

// Icon mapping
const iconMap = {
  Shield,
  Eye,
  Lock,
  Database,
  Users,
  Mail,
}

const defaultSections = [
    {
      title: "Information We Collect",
      icon: Database,
      content: [
        "Personal information you provide when creating an account (name, email, phone number)",
        "Billing and shipping addresses for order processing",
        "Payment information (processed securely through our payment partners)",
        "Communication preferences and marketing consent",
        "Product reviews and feedback you submit",
        "Website usage data and analytics information",
      ],
    },
    {
      title: "How We Use Your Information",
      icon: Users,
      content: [
        "Process and fulfill your orders and provide customer service",
        "Send order confirmations, shipping updates, and important account information",
        "Personalize your shopping experience and product recommendations",
        "Send marketing communications (only with your consent)",
        "Improve our website, products, and services",
        "Comply with legal obligations and prevent fraud",
      ],
    },
    {
      title: "Information Sharing",
      icon: Shield,
      content: [
        "We never sell your personal information to third parties",
        "Trusted service providers who help us operate our business (shipping, payment processing)",
        "Legal authorities when required by law or to protect our rights",
        "Business partners only with your explicit consent",
        "Aggregated, non-personal data for analytics and research purposes",
      ],
    },
    {
      title: "Data Security",
      icon: Lock,
      content: [
        "Industry-standard encryption for all data transmission",
        "Secure servers with regular security updates and monitoring",
        "Limited access to personal information on a need-to-know basis",
        "Regular security audits and vulnerability assessments",
        "Secure payment processing through PCI-compliant providers",
        "Data backup and recovery procedures to prevent data loss",
      ],
    },
  ]

export default function PrivacyPolicy() {
  const [pageData, setPageData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const res = await axios.get("/legal/privacy")
        setPageData(res.data.page)
      } catch (error) {
        console.error("Error fetching privacy policy:", error)
        setPageData(null)
      } finally {
        setLoading(false)
      }
    }
    fetchPage()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  const sections = pageData?.sections || defaultSections
  const heroTitle = pageData?.hero_title || "Privacy Policy"
  const heroSubtitle = pageData?.hero_subtitle || "We are committed to protecting your privacy and ensuring the security of your personal information. Learn how we collect, use, and safeguard your data."
  const heroBadgeText = pageData?.hero_badge_text || "Your Privacy Matters"
  
  // Format lastUpdated to show only date in "December 15, 2024" format
  const formatDate = (dateString) => {
    if (!dateString) return "December 15, 2024"
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return dateString // Return as-is if invalid date
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    } catch {
      return dateString || "December 15, 2024"
    }
  }
  const lastUpdated = formatDate(pageData?.last_updated)
  const introductionTitle = pageData?.introduction_title || "Introduction"
  const introductionContent = pageData?.introduction_content || "At Fayoz, we respect your privacy and are committed to protecting your personal information."
  const rights = pageData?.additional_content?.rights || [
    { title: "Access & Correction", description: "Request access to your personal data and correct any inaccuracies." },
    { title: "Data Portability", description: "Request a copy of your data in a structured, machine-readable format." },
    { title: "Deletion", description: "Request deletion of your personal data, subject to legal requirements." },
    { title: "Marketing Opt-out", description: "Unsubscribe from marketing communications at any time." },
  ]
  const contactTitle = pageData?.contact_title || "Questions About Privacy?"
  const contactSubtitle = pageData?.contact_subtitle || "If you have any questions about this Privacy Policy or our data practices, please don't hesitate to contact us."
  const contactEmail = pageData?.contact_email || "privacy@fayozltd.com"

  return (
    <>
      <Seo {...routeSeoConfig.privacy} type="website" />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {heroBadgeText && (
              <div className="inline-flex items-center gap-2 bg-secondary/20 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                {heroBadgeText}
              </div>
            )}
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 text-balance">
              {heroTitle}
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto text-pretty">
              {heroSubtitle}
            </p>
            <p className="text-sm text-gray-500">Last updated: {lastUpdated}</p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <Card className="mb-12 border-0 shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{introductionTitle}</h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {introductionContent}
                </p>
              </CardContent>
            </Card>

            {/* Privacy Sections */}
            <div className="space-y-8">
              {sections.map((section, index) => {
                const IconComponent = iconMap[section.icon] || Shield
                return (
                  <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                    <CardHeader className="pb-6">
                      <CardTitle className="flex items-center gap-3 text-2xl text-gray-900">
                        <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                        {section.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {section.content.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start gap-3">
                            <div className="w-2 h-2 bg-secondary rounded-full mt-2 flex-shrink-0"></div>
                            <span className="text-gray-600 leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )
              })}
            </div>

            {/* Your Rights */}
            <Card className="mt-12 border-0 shadow-lg bg-gradient-to-br from-primary/5 to-secondary/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl text-gray-900">
                  <Eye className="w-8 h-8 text-primary" />
                  Your Rights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {rights.map((right, index) => (
                    <div key={index}>
                      <h4 className="font-semibold text-gray-900 mb-2">{right.title}</h4>
                      <p className="text-gray-600 text-sm">{right.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Contact Section */}
            <Card className="mt-12 border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <Mail className="w-12 h-12 text-secondary mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{contactTitle}</h3>
                {contactSubtitle && (
                  <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                    {contactSubtitle}
                  </p>
                )}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/contact-us">
                    <Button size="lg" className="bg-primary hover:bg-primary/90 text-white">
                      Contact Us
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  {contactEmail && (
                    <Button
                      variant="outline"
                      size="lg"
                      className="border-primary text-primary hover:bg-primary/5 bg-transparent"
                    >
                      Email: {contactEmail}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}
